import type {
  AIEvent,
  Conversation,
  ConversationMessage,
  SendMessageRequest,
} from '@recipes/contracts';
import { Alert, Badge, Button, Spinner, Textarea, cn } from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { ProposalCard, type ApplyMode } from '@/features/ai/proposal-diff';
import { formatDate } from '@/features/shared/labels';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { ProcessingIndicator } from '@/features/shared/processing-indicator';
import { api } from '@/lib/api';
import { describeError, isVersionConflict, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { useSse } from '@/lib/sse/use-sse';
import { toast } from '@/components/feedback/toaster';

type Intent = NonNullable<SendMessageRequest['intent']>;
const INTENTS: Intent[] = [
  'SUBSTITUTE',
  'ADAPT_SERVINGS',
  'ADAPT_DIET',
  'ADAPT_TIME',
  'ADAPT_EQUIPMENT',
  'EXPLAIN',
  'LEFTOVERS',
  'VARIATION',
];

function resolveStreamUrl(streamUrl: string): string {
  return /^https?:\/\//.test(streamUrl) ? streamUrl : api.http.url(streamUrl);
}

export function ConversationPage() {
  const { conversationId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const key = queryKeys.ai.conversation(conversationId);

  const conversationQuery = useQuery({
    queryKey: key,
    queryFn: () => api.ai.conversation(conversationId),
    enabled: Boolean(conversationId),
  });
  const conversation = conversationQuery.data;
  const recipeId = conversation?.recipeId ?? null;
  const recipeQuery = useQuery({
    queryKey: queryKeys.recipes.detail(recipeId ?? ''),
    queryFn: () => api.recipes.get(recipeId ?? ''),
    enabled: Boolean(recipeId),
  });

  const [content, setContent] = useState('');
  const [intent, setIntent] = useState<Intent>('CHAT');
  const [stream, setStream] = useState<{ url: string; text: string; error: string | null } | null>(
    null,
  );
  const [applying, setApplying] = useState<{ messageId: string; mode: ApplyMode } | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const onEvent = useCallback(
    (event: AIEvent) => {
      if (event.type === 'delta') setStream((s) => (s ? { ...s, text: s.text + event.text } : s));
      if (event.type === 'error') setStream((s) => (s ? { ...s, error: event.message } : s));
      if (event.type === 'done' || event.type === 'result') {
        void queryClient.invalidateQueries({ queryKey: key }).then(() => setStream(null));
      }
    },
    [key, queryClient],
  );
  const sse = useSse<AIEvent>(stream?.url ?? null, {
    onEvent,
    isTerminal: (e) => e.type === 'done' || e.type === 'error',
    onError: () => undefined,
  });
  // When the stream cannot be opened at all, fall back to refetching the conversation.
  useEffect(() => {
    if (sse.status !== 'error' || !stream) return;
    const id = setInterval(() => void queryClient.invalidateQueries({ queryKey: key }), 2000);
    const stop = setTimeout(() => {
      clearInterval(id);
      setStream((s) => (s ? { ...s, error: t('errors.PROVIDER_UNAVAILABLE') } : s));
    }, 60_000);
    return () => {
      clearInterval(id);
      clearTimeout(stop);
    };
  }, [sse.status, stream, key, queryClient, t]);
  // Once the assistant message is persisted, hide the transient stream bubble.
  useEffect(() => {
    if (!stream || !conversation) return;
    const last = conversation.messages[conversation.messages.length - 1];
    if (
      last?.role === 'assistant' &&
      stream.text &&
      last.content.startsWith(stream.text.slice(0, 20))
    )
      setStream(null);
  }, [conversation, stream]);

  const send = useMutation({
    mutationFn: (body: SendMessageRequest) => api.ai.sendMessage(conversationId, body),
    onSuccess: (res) => {
      queryClient.setQueryData<Conversation>(key, (prev) =>
        prev ? { ...prev, messages: [...prev.messages, res.userMessage] } : prev,
      );
      setContent('');
      setIntent('CHAT');
      setStream({ url: resolveStreamUrl(res.streamUrl), text: '', error: null });
    },
    onError: (e) => toastError(e, t('assistant.sendFailed')),
  });

  const apply = useMutation({
    mutationFn: (input: { messageId: string; mode: ApplyMode; variationLabel?: string }) =>
      api.ai.applyProposal(conversationId, {
        messageId: input.messageId,
        mode: input.mode,
        expectedVersion: input.mode === 'APPLY_TO_RECIPE' ? recipeQuery.data?.version : undefined,
        variationLabel: input.variationLabel,
      }),
    onSuccess: (recipe, input) => {
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
      toast.success(t(`assistant.proposal.done.${input.mode}`));
      if (input.mode !== 'APPLY_TO_RECIPE') navigate(`/recipes/${recipe.id}`);
    },
    onError: (e) => {
      if (isVersionConflict(e)) {
        toast.error(t('errors.VERSION_CONFLICT'));
        void recipeQuery.refetch();
      } else toastError(e);
    },
    onSettled: () => setApplying(null),
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [conversation?.messages.length, stream?.text]);

  if (conversationQuery.isLoading) {
    return (
      <PageContainer>
        <Spinner label={t('app.loading')} />
      </PageContainer>
    );
  }
  if (!conversation) {
    return (
      <PageContainer>
        <Alert variant="destructive">{describeError(conversationQuery.error)}</Alert>
      </PageContainer>
    );
  }
  const busy = send.isPending || (Boolean(stream) && !stream?.error);

  return (
    <PageContainer className="flex max-w-3xl flex-col">
      <FeaturePageHeader
        title={conversation.title || t('assistant.untitled')}
        backTo="/assistant"
        description={
          recipeId ? (
            <>
              {t('assistant.boundTo')}{' '}
              <Link to={`/recipes/${recipeId}`} className="text-primary underline">
                {recipeQuery.data?.title ?? t('app.loading')}
              </Link>
            </>
          ) : undefined
        }
      />
      <ol className="flex-1 space-y-3" aria-label={t('assistant.thread')} aria-live="polite">
        {conversation.messages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            hasRecipe={Boolean(recipeId)}
            applying={applying?.messageId === m.id ? applying.mode : null}
            onApply={(mode, label) => {
              setApplying({ messageId: m.id, mode });
              apply.mutate({ messageId: m.id, mode, variationLabel: label });
            }}
            locale={i18n.language}
          />
        ))}
        {stream && (
          <li className="mr-8 rounded-lg border bg-card p-3 text-sm" data-testid="streaming-reply">
            {stream.error ? (
              <span role="alert" className="text-destructive">
                {stream.error}
              </span>
            ) : (
              <>
                {stream.text ? (
                  <>
                    <span className="whitespace-pre-wrap">{stream.text}</span>
                    <span className="ml-1 inline-block animate-pulse" aria-hidden>
                      ▍
                    </span>
                    <span className="sr-only">{t('assistant.streaming')}</span>
                  </>
                ) : (
                  <ProcessingIndicator compact label={t('assistant.thinking')} />
                )}
              </>
            )}
          </li>
        )}
        {send.isPending && !stream && (
          <li className="mr-8 rounded-lg border bg-card p-3 text-sm" data-testid="sending-reply">
            <ProcessingIndicator compact label={t('assistant.thinking')} />
          </li>
        )}
        <div ref={bottomRef} />
      </ol>

      <form
        className="sticky bottom-20 mt-4 space-y-2 rounded-lg border bg-background/95 p-3 shadow-md backdrop-blur md:bottom-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (content.trim()) send.mutate({ content: content.trim(), intent });
        }}
      >
        <div
          role="radiogroup"
          aria-label={t('assistant.intent')}
          className="flex gap-2 overflow-x-auto pb-1"
        >
          {INTENTS.map((i) => (
            <button
              key={i}
              type="button"
              role="radio"
              aria-checked={intent === i}
              onClick={() => setIntent((cur) => (cur === i ? 'CHAT' : i))}
              className={cn(
                'min-h-[36px] shrink-0 rounded-full border px-3 text-xs',
                intent === i
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'bg-background hover:bg-accent',
              )}
            >
              {t(`assistant.intents.${i}`)}
            </button>
          ))}
        </div>
        <label htmlFor="assistant-input" className="sr-only">
          {t('assistant.messageField')}
        </label>
        <div className="flex items-end gap-2">
          <Textarea
            id="assistant-input"
            rows={2}
            value={content}
            maxLength={8000}
            placeholder={t('assistant.placeholder')}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && content.trim())
                send.mutate({ content: content.trim(), intent });
            }}
          />
          <Button
            type="submit"
            size="icon-lg"
            aria-label={t('assistant.send')}
            loading={busy}
            disabled={!content.trim()}
          >
            <Send aria-hidden />
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}

function MessageBubble({
  message,
  hasRecipe,
  applying,
  onApply,
  locale,
}: {
  message: ConversationMessage;
  hasRecipe: boolean;
  applying: ApplyMode | null;
  onApply: (mode: ApplyMode, label?: string) => void;
  locale: string;
}) {
  const { t } = useTranslation();
  const mine = message.role === 'user';
  return (
    <li
      className={cn('rounded-lg p-3 text-sm', mine ? 'ml-8 bg-primary/10' : 'mr-8 border bg-card')}
    >
      <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
        <Badge variant={mine ? 'default' : 'secondary'}>
          {t(`assistant.roles.${message.role}`)}
        </Badge>
        <time dateTime={message.createdAt}>{formatDate(message.createdAt, locale, true)}</time>
      </div>
      {/* The assistant answers in the language it was addressed in – not always the UI language. */}
      <p className="whitespace-pre-wrap" lang={message.language ?? undefined}>
        {message.content}
      </p>
      {message.proposal && (
        <ProposalCard
          proposal={message.proposal}
          hasRecipe={hasRecipe}
          applying={applying}
          onApply={onApply}
        />
      )}
    </li>
  );
}
